import { useState, useEffect, useMemo } from "react";
import { useMediaQuery } from "@mantine/hooks";
import {
  Table,
  UnstyledButton,
  Group,
  Text,
  TextInput,
  rem,
  Paper,
  Button,
  Checkbox,
  ActionIcon,
} from "@mantine/core";
import iconSearch from "../../../assets/svg/search.svg";
import MainLoader from "../Loader/Loader";
import { FilterMenu } from "../FilterMenu/FilterMenu";
import Actions from "./Actions";
import StatusText from "./StatusText";
import { FormatDate } from "../../../helper/DateFormatting";
import { useLocation, useNavigate } from "react-router-dom";
import { getListNavigationState } from "../../utils/listNavigation";

import classes from "./DataTable.module.css";

const formatTableHeaderLabel = (label) => {
  if (typeof label !== "string" || !label || label !== label.toUpperCase()) return label;

  return label
    .split(" ")
    .map((word) => {
      if (!word || word === "#") return word;
      const lower = word.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
};

function Th({
  children,
  sortable,
  active,
  direction,
  onClick,
  controlStyle,
  labelStyle,
  ...props
}) {
  return (
    <Table.Th className={classes.th} {...props}>
      <UnstyledButton
        className={`${classes.control} ${
          sortable ? classes.sortable : classes.notSortable
        }`}
        style={controlStyle}
        disabled={!sortable}
        onClick={sortable ? onClick : undefined}
      >
        <Group justify="space-between" wrap="nowrap">
          <Text
            fw={500}
            fz="sm"
            component="span"
            style={{ whiteSpace: "nowrap", ...labelStyle }}
          >
            {formatTableHeaderLabel(children)}
          </Text>
          {sortable && (
            <Text
              fz="xs"
              c={active ? "#033F7C" : "#9BB3C7"}
              className={classes.sortIndicator}
            >
              {active ? (direction === "desc" ? "↓" : "↑") : "↕"}
            </Text>
          )}
        </Group>
      </UnstyledButton>
    </Table.Th>
  );
}

// Utility function to get nested value
const getNestedValue = (obj, path) => {
  return path.split(".").reduce((acc, part) => acc && acc[part], obj);
};

const DataTable = ({
  data,
  config,
  loading,
  form,
  filterOptions,
  onSubmit,
  onSearch,
  toolbarLeftContent,
  toolbarRightContent,
  toolbarBottomContent,
  searchPlaceholder,
  searchValue,
  onSelectionChange,
  onOrdering,
  orderingValue,
  /** Toolbar row vertical padding (search / actions). Default matches existing lists. */
  // toolbarPy = "xl",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const safeData = Array.isArray(data) ? data : [];
  const [sortedData, setSortedData] = useState(safeData);
  const [selection, setSelection] = useState([]);
  const isNarrowViewport = useMediaQuery("(max-width: 48em)");
  const tableMinWidth = config?.tableMinWidth;

  const actionsColWidth = config.actionsColumnWidth ?? 72;
  const stickyActions = config.stickyActions !== false;
  const fieldCount = config.fields?.length ?? 0;
  const selectionExtra = config.selectionCheckbox ? 48 : 0;
  const actionsExtra =
    (config.actions?.length || config.handler?.length ? 1 : 0) * actionsColWidth;

  const autoMobileTableMinWidth = useMemo(() => {
    if (!isNarrowViewport) return undefined;
    const perCol =
      fieldCount >= 7 ? 132 : fieldCount >= 5 ? 118 : fieldCount >= 3 ? 102 : 92;
    const w = fieldCount * perCol + selectionExtra + actionsExtra;
    return Math.min(1600, Math.max(440, w));
  }, [
    isNarrowViewport,
    fieldCount,
    selectionExtra,
    actionsExtra,
  ]);

  const effectiveTableMinWidth = tableMinWidth ?? autoMobileTableMinWidth;

  const tableStyle = useMemo(
    () => ({
      width: "100%",
      minWidth:
        effectiveTableMinWidth ||
        fieldCount * 140 + selectionExtra + actionsExtra, // 👈 dynamic width
    }),
    [effectiveTableMinWidth, fieldCount, selectionExtra, actionsExtra]
  );

  const currentSort = orderingValue?.startsWith("-")
    ? { key: orderingValue.slice(1), direction: "desc" }
    : orderingValue
    ? { key: orderingValue, direction: "asc" }
    : { key: "", direction: "" };
 
  useEffect(() => {
    if (!orderingValue) {
      setSortedData(data);
      return;
    }

    const isDesc = orderingValue.startsWith("-");
    const sortKey = isDesc ? orderingValue.slice(1) : orderingValue;

    const sorted = [...data].sort((a, b) => {
      const aVal = getNestedValue(a, sortKey);
      const bVal = getNestedValue(b, sortKey);

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      const aNum = Number(aVal);
      const bNum = Number(bVal);
      const bothNumeric =
        !Number.isNaN(aNum) &&
        !Number.isNaN(bNum) &&
        aVal !== "" &&
        bVal !== "";

      if (bothNumeric) {
        return isDesc ? bNum - aNum : aNum - bNum;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return isDesc ? 1 : -1;
      if (aStr > bStr) return isDesc ? -1 : 1;
      return 0;
    });

    setSortedData(sorted);
  }, [data, orderingValue]);

  const selectRow = (id) => {
    setSelection((current) => {
      const newSelection = current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];

      if (onSelectionChange) {
        onSelectionChange(newSelection);
      }

      return newSelection;
    });
  };

  const selectAllRow = () => {
    setSelection((current) => {
      const allSelected = current.length === safeData.length;
      const newSelection = allSelected ? [] : safeData.map((item) => item.id);

      if (onSelectionChange) {
        onSelectionChange(newSelection);
      }

      return newSelection;
    });
  };

  const handleSelectionAction = (handler) => {
    if (handler) {
      handler(selection);
    }
  };

  const hasRowAction =
    typeof config.onRowClick === "function" ||
    typeof config.rowClickTo === "function";
  const useDoubleClickRow = config.doubleClickRow === true;

  const getRowStyles = (row, index) => {
    const baseStyle = {
      backgroundColor: index % 2 === 0 ? "#F8F8F8" : "#FFF",
      cursor: hasRowAction ? "pointer" : "default",
    };

    if (typeof config.getRowStyle === "function") {
      return {
        ...baseStyle,
        ...config.getRowStyle(row, index),
      };
    }

    return baseStyle;
  };

  const runRowAction = (row) => {
    const listState = getListNavigationState(location);

    if (typeof config.onRowClick === "function") {
      config.onRowClick(row, listState);
      return;
    }
    if (typeof config.rowClickTo === "function") {
      const target = config.rowClickTo(row);
      if (target) {
        navigate(target, { state: listState });
      }
    }
  };

  const rows = (Array.isArray(sortedData) ? sortedData : []).map((row, index) => (
    <Table.Tr
      key={index}
      onClick={!useDoubleClickRow && hasRowAction ? () => runRowAction(row) : undefined}
      onDoubleClick={useDoubleClickRow && hasRowAction ? () => runRowAction(row) : undefined}
      style={getRowStyles(row, index)}
    >
      {config.selectionCheckbox && (
        <Table.Td style={config.selectionCellStyle}>
          <Checkbox
            size={config.selectionCheckboxSize}
            radius={config.selectionCheckboxRadius ?? "sm"}
            checked={selection.includes(row.id)}
            onChange={() => selectRow(row.id)}
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
            styles={config.selectionCheckboxStyles}
          />
        </Table.Td>
      )}

                {(config.fields ?? []).map((field) => (
        <Table.Td
          key={`${row.id}-${field.key}`}
          onClick={field.stopRowClick ? (event) => event.stopPropagation() : undefined}
          onDoubleClick={field.stopRowClick ? (event) => event.stopPropagation() : undefined}
          style={{
            ...(field.width
              ? { width: field.width, minWidth: field.width, maxWidth: field.width }
              : {}),
            ...field.cellStyle,
          }}
        >

          {field.type === "combine" ? (
            <>
              <Text size="sm" me="xs" my={"9px"} component="span">
                {getNestedValue(row, field.key1)}
              </Text>
              <Text size="sm" component="span">
                {getNestedValue(row, field.key2)}
              </Text>
            </>
          ) : field.type === "date" ? (
            <Text size="sm" my={"9px"} component="span"  style={{ whiteSpace: "nowrap" }}>
              {FormatDate(getNestedValue(row, field.key), field.type)}
            </Text>
          ) : field.type === "datetime" ? (
            <Text size="sm" my={"9px"} component="span" c="#6C94B4" fw={500} style={{ whiteSpace: "nowrap" }}>
              {FormatDate(getNestedValue(row, field.key), field.type)}
            </Text>
          ) : field.type === "status" ? (
            <StatusText
              status={getNestedValue(row, field.key)}
              field={field.key}
            />
          ):  field.render ? (
                  field.render(row)
                ) : (
                <Text
                  size="sm"
                  my="9px"
                  fw={field.key === config.fields?.[0]?.key ? 500 : 500}
                  c={field.key === config.fields?.[0]?.key ? "#092C4C" : "#6C94B4"}
                  component="span"
                  style={{
                    display: "block",
                    maxWidth: "100%",
                    whiteSpace: field.wrap ? "normal" : field.nowrap ? "nowrap" : "ellipsis",
                    wordBreak: field.wrap ? "normal" : "break-word",
                    overflowWrap: field.wrap ? "break-word" : undefined,
                  }}
                >
                  {getNestedValue(row, field.key)}
                </Text>       
          )}
        </Table.Td>
      ))}

      {(config.actions || config.handler) && (
        <Table.Td
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={(event) => event.stopPropagation()}
          style={{
            ...(stickyActions
              ? {
                  position: "sticky",
                  right: 0,
                  backgroundColor: "var(--mantine-color-body)",
                  zIndex: 1,
                }
              : {}),
            whiteSpace: "nowrap",
            minWidth: rem(actionsColWidth),
            width: rem(actionsColWidth),
            textAlign: "center",
            paddingLeft: rem(8),
            paddingRight: rem(8),
          }}
        >
          <Group gap={0} justify="center">
            {config.actionsDisplay === "inline" &&
            (config.handler || []).length > 0 ? (
              (config.handler || []).map((h, idx) => (
                <UnstyledButton
                  key={idx}
                  onClick={() => h.handler(row)}
                  style={{ padding: 0 }}
                >
                  <Text size="sm" fw={600} c="red">
                    {h.label}
                  </Text>
                </UnstyledButton>
              ))
            ) : (
              <Actions
                rowData={row}
                actions={config.actions || []}
                handler={config.handler || []}
              />
            )}
          </Group>
        </Table.Td>
      )}
    </Table.Tr>
  ));

  const getSortKey = (field) => field.sortKey || field.key || field.key1;
  const searchInputStyles = config.searchInputStyles || {
    input: {
      backgroundColor: "#fff",
      padding: "8px 34px",
      borderRadius: "8px",
      border: "1px solid rgba(108, 148, 180, 0.15)",
    },
  };

  const handleHeaderSort = (field) => {
    const key = getSortKey(field);
    if (!key || typeof onOrdering !== "function") return;
    const isActive = currentSort.key === key;
    const nextDirection =
      isActive && currentSort.direction === "asc" ? "desc" : "asc";
    const nextValue = nextDirection === "desc" ? `-${key}` : key;
    onOrdering(nextValue);
  };

  return (
    <>
      <Group
        justify="space-between"
        align="center"
        wrap={config.toolbarNoWrap ? "nowrap" : "wrap"}
        className={config.toolbarRowClassName}
      >
        <Group
          gap={config.toolbarLeftGap ?? "md"}
          align="center"
          wrap={config.toolbarNoWrap ? "nowrap" : "wrap"}
          style={config.toolbarNoWrap ? { flex: 1, minWidth: 0 } : undefined}
        >
          <TextInput
            placeholder={searchPlaceholder || config.searchPlaceholder || "Search here..."}
            styles={searchInputStyles}
            variant="default"
            {...(searchValue !== undefined ? { value: searchValue } : {})}
            leftSection={
              <img
                style={{
                  width: config.searchIconSize || rem(16),
                  height: config.searchIconSize || rem(16),
                }}
                src={iconSearch}
              />
            }
            onChange={onSearch}
            w={config.searchInputWidth}
            h={config.searchInputHeight}
          />
          {toolbarLeftContent}

          {filterOptions && (
            <FilterMenu
              form={form}
              filterOptions={filterOptions}
              onSubmit={onSubmit}
              triggerProps={config.filterTriggerProps}
              iconProps={config.filterIconProps}
            />
          )}
          {!filterOptions && config.showFilterButton && (
            <ActionIcon
              variant="filled"
              color="#FFFFFF"
              radius={8}
              w={48}
              h={48}
              p={0}
              aria-label="filter"
              style={{ border: "1px solid rgba(108, 148, 180, 0.15)" }}
            >
              <img src="/images/svg/sort.svg" style={{ width: 18, height: 18 }} />
            </ActionIcon>
          )}

        </Group>
        {toolbarRightContent || config.selectionActions?.length > 0 ? (
        <Group gap={config.toolbarRightGap ?? "sm"} align="center" wrap="wrap">
          {toolbarRightContent}
          {config.selectionActions?.length > 0 && (
            <Group>
              {config.selectionActions.map((action, index) => (
                <Button
                  key={index}
                  variant="gradient"
                  size="lg"
                  fz={14}
                  fw={600}
                  onClick={() => handleSelectionAction(action.handler)}
                  {...config.selectionActionButtonProps}
                >
                  {action.label}
                </Button>
              ))}
            </Group>
          )}
        </Group>
        ) : null}
      </Group>
      {toolbarBottomContent}
      <Paper mt={config.tableMt ?? "lg"} styles={{ root: { maxWidth: "100%", overflow: "hidden" } }}>
        <div className={classes.tableWrapper}>
          <Table
            horizontalSpacing={config.horizontalSpacing || "md"}
            verticalSpacing={config.verticalSpacing || "xs"}
            // miw={700}
            // layout="fixed"
            withRowBorders={false}
            highlightOnHover={config.highlightOnHover ?? true}
            styles={config.tableStyles}
            style={{ ...tableStyle, tableLayout: "fixed" }}
          >
            <Table.Thead className={classes.theadSticky}>
              <Table.Tr>
                {config.selectionCheckbox && (
                  <Table.Th
                    style={{
                      width: config.selectionColumnWidth || rem(40),
                      minWidth: config.selectionColumnWidth || rem(40),
                      whiteSpace: "nowrap",
                      ...config.selectionHeaderStyle,
                    }}
                  >
                    <Checkbox
                      size={config.selectionCheckboxSize}
                      radius={config.selectionCheckboxRadius ?? "sm"}
                      onChange={selectAllRow}
                      checked={selection.length === safeData.length}
                      indeterminate={
                        selection.length > 0 && selection.length !== safeData.length
                      }
                      styles={config.selectionCheckboxStyles}
                    />
                  </Table.Th>
                )}

                {(config.fields ?? []).map((field, index) => {
                  const sortKey = getSortKey(field);
                  const isSortable =
                    field.sortable === false ? false : Boolean(sortKey);
                  const isActive = currentSort.key === sortKey;
                  return (
                  <Th
                    key={`${field.label}${index}`}
                    sortable={isSortable}
                    active={isActive}
                    direction={currentSort.direction}
                    onClick={() => handleHeaderSort(field)}
                    style={{
                      ...(field.width
                        ? {
                            width: field.width,
                            minWidth: field.width,
                            maxWidth: field.width,
                          }
                        : {}),
                      ...field.headerStyle,
                    }}
                    controlStyle={field.headerControlStyle}
                    labelStyle={field.labelStyle}
                  >
                    {field.label}
                  </Th>
                  );
                })}

                {(config.actions || config.handler) && (
                  <Th
                    style={{
                      ...(stickyActions
                        ? {
                            position: "sticky",
                            right: 0,
                            backgroundColor: "var(--mantine-color-body)",
                            zIndex: 2,
                          }
                        : {}),
                      minWidth: rem(actionsColWidth),
                      width: rem(actionsColWidth),
                      textAlign: "center",
                    }}
                  >
                    <Text fw={500} fz="xs" component="span" hiddenFrom="sm">
                      Actions
                    </Text>
                     <Text fw={500} fz="sm" component="span" visibleFrom="sm">
                      Actions
                    </Text>
                  </Th>
                )}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td
                    colSpan={
                      (config.fields ?? []).length +
                      (config.actions || config.handler ? 1 : 0) +
                      (config.selectionCheckbox ? 1 : 0)
                    }
                  >
                    <MainLoader />
                  </Table.Td>
                </Table.Tr>
              ) : rows.length > 0 ? (
                rows
              ) : (
                <Table.Tr>
                  <Table.Td
                    colSpan={
                      (config.fields ?? []).length +
                      (config.actions || config.handler ? 1 : 0) +
                      (config.selectionCheckbox ? 1 : 0)
                    }
                  >
                    <Text
                      fw={500}
                      component="span"
                      style={{ display: "block", textAlign: "center" }}
                    >
                      Nothing found
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </div>
      </Paper>
    </>
  );
};

export default DataTable;